/**
 * @module main
 * Fruit Slasher — integrated game entry point.
 *
 * Wiring order:
 *  1. Privacy / onboarding screen (StartScreen) shown BEFORE getUserMedia (T-01).
 *  2. Camera + MediaPipe HandLandmarker init after user consent.
 *  3. StateMachine drives screen visibility and input routing.
 *  4. Decoupled tracking loop (~30fps) + render loop (60fps via rAF).
 *  5. All gameplay systems (Spawner, ScoreManager, PowerUpManager, etc.) tick
 *     in the render loop scaled by SlowMo.timeScale.
 *  6. Mouse/pointer fallback (dev-only) so the game is testable without camera.
 *
 * ACCEPTANCE: boots to StartScreen → full golden path playable via mouse:
 *   menu → mode select → Classic → fruits fly → slash → split+particles → score
 *   → bomb → game over → leaderboard → share → retry. Zen timer path also works.
 *   prefers-reduced-motion respected. Pipeline green.
 */

// ---------------------------------------------------------------------------
// Core framework
// ---------------------------------------------------------------------------

import { StateMachine } from './game/StateMachine.js';
import { GameLoop } from './game/GameLoop.js';

// ---------------------------------------------------------------------------
// Input
// ---------------------------------------------------------------------------

import { InputRouter } from './input/inputRouter.js';
import { DwellClickManager } from './input/dwellClick.js';
import { processFrame as gestureProcessFrame, resetSlashState } from './input/gestureEngine.js';

// ---------------------------------------------------------------------------
// Tracking
// ---------------------------------------------------------------------------

import { startCamera, stopCamera, CameraError } from './camera.js';
import { initHandTracker, detectFrame, destroyHandTracker, isReady as trackerReady } from './tracking/handTracker.js';
import { OneEuroFilter2D } from './tracking/smoothing.js';
import { assignHands, resetHandIdentity } from './tracking/handIdentity.js';
import type { HandCandidate } from './tracking/handIdentity.js';
import { normToScreen, INDEX_FINGERTIP_LANDMARK } from './tracking/pointer.js';
import { PointerInterpolator } from './utils/interpolate.js';
import { segmentIntersectsCircle, distPointToSegment } from './utils/collision.js';
import { initDebugOverlay, debugLog, setDebugStats } from './ui/debugOverlay.js';
import {
  isWorkerTrackingSupported,
  init as workerTrackerInit,
  sendFrame,
  destroy as workerTrackerDestroy,
} from './tracking/workerTracker.js';

// ---------------------------------------------------------------------------
// Gameplay systems
// ---------------------------------------------------------------------------

import { Spawner } from './game/Spawner.js';
import { ScoreManager } from './game/ScoreManager.js';
import { PowerUpManager } from './game/PowerUpManager.js';
import { FrenzyManager } from './game/FrenzyManager.js';
import { ObstacleManager } from './game/ObstacleManager.js';
import { BladeTrail } from './game/Blade.js';
import { ComboPopupManager } from './game/ComboPopup.js';
import { createFruit, updateFruit, sliceFruit, destroyFruit } from './game/Fruit.js';
import { createBomb, updateBomb, destroyBomb } from './game/Bomb.js';
import { createPowerUp, updatePowerUp, destroyPowerUp } from './game/PowerUp.js';
import type { PowerUpType } from './game/PowerUp.js';
import { loadTextures, fruitWholeKey, fruitHalfAKey, fruitHalfBKey } from './AssetLoader.js';
import type { TextureRegistry } from './AssetLoader.js';

// ---------------------------------------------------------------------------
// FX
// ---------------------------------------------------------------------------

import { HitStop } from './fx/HitStop.js';
import { ScreenShake } from './fx/ScreenShake.js';
import { SlowMo } from './fx/SlowMo.js';
import { AudioManager } from './fx/AudioManager.js';
import { ParticleSystem } from './fx/Particles.js';
import { renderShareCard, shareOrDownload } from './fx/ShareCard.js';
import { createNativeAudioPlayer } from './fx/audioPlayer.js';

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------

import { LocalLeaderboard } from './ui/Leaderboard.js';

// ---------------------------------------------------------------------------
// Config & types
// ---------------------------------------------------------------------------

import { CONFIG } from './game-config.js';
import type { GameMode } from './game-config.js';
import { STRINGS } from './content/strings.js';
import type { Bomb, Fruit, HandPointer, PowerUp, SlashSegment } from './types.js';

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when the slash segment intersects a circle at (cx, cy, r).
 * Delegates to the TESTED closest-point implementation in utils/collision —
 * a previous local quadratic-root version returned false when the segment
 * STARTED inside the circle (pointer resting on a fruit, then flicking),
 * which made objects feel randomly unhittable.
 */
function segmentHitsCircle(
  seg: SlashSegment,
  cx: number,
  cy: number,
  r: number,
): boolean {
  return segmentIntersectsCircle(seg.from, seg.to, { x: cx, y: cy }, r);
}

// ---------------------------------------------------------------------------
// Boot — check prefers-reduced-motion once
// ---------------------------------------------------------------------------

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Coarse primary pointer = phone/tablet — enables the mobile perf profile:
 * single-hand tracking, pre-rendered shadows, fewer particles, unfiltered
 * camera feed. Desktop (fine pointer) keeps the full visual treatment.
 */
const IS_MOBILE = window.matchMedia('(pointer: coarse)').matches;

/** Juice-particle count multiplier (mobile renders fewer). */
const PARTICLE_SCALE = IS_MOBILE ? CONFIG.perf.mobile.particleScale : 1;

/** Shared options for dropout coasting (hoisted — zero per-frame allocs). */
const COAST_OPTS = {
  maxCoastMs: CONFIG.tracking.coastMs,
  tauMs: CONFIG.tracking.coastTauMs,
} as const;

/**
 * Cursor opacity for a pointer of the given age: solid while fresh, then a
 * fade through the coast window — tracking dropouts MELT the cursor away
 * instead of blinking it out (fast hands motion-blur the camera frame and
 * MediaPipe loses them for a few frames; vanishing reads as a bug).
 */
function cursorAlpha(ageMs: number): number {
  const fadeStart = CONFIG.tracking.coastMs - CONFIG.ui.cursorFadeOutMs;
  if (ageMs <= fadeStart) return 1;
  return Math.max(0, 1 - (ageMs - fadeStart) / CONFIG.ui.cursorFadeOutMs);
}

/**
 * Pre-rendered shadow/glow blobs for the mobile cheap-shadow path: one 64×64
 * radial gradient rendered ONCE, then drawImage'd under each sprite.
 * drawImage is fast everywhere; ctx.shadowBlur per sprite is not.
 */
function makeBlob(inner: string, outer: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(32, 32, 6, 32, 32, 32);
  grad.addColorStop(0, inner);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  return c;
}
const shadowBlob = makeBlob(`rgba(0,0,0,${CONFIG.ui.spriteShadowAlpha})`, 'rgba(0,0,0,0)');
const goldBlob = makeBlob('rgba(255,215,0,0.5)', 'rgba(255,215,0,0)');

/** Dev mode flag — gates the debug overlay, hitbox rings, and slash logs. */
const DEV = import.meta.env.DEV;

/** Frame counters sampled by the debug overlay (dev only). */
let renderFrameCount = 0;
let trackFrameCount = 0;

// ---------------------------------------------------------------------------
// Mount point
// ---------------------------------------------------------------------------

const mount = document.getElementById('app');
if (!mount) throw new Error('Missing #app mount in index.html');

// ---------------------------------------------------------------------------
// Overlay canvas (pointer cursor + blade trail + particles + HUD)
// ---------------------------------------------------------------------------

const canvas = document.createElement('canvas');
canvas.style.cssText = 'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:10;';
document.body.appendChild(canvas);
const ctx2d = canvas.getContext('2d')!;

function resizeCanvas(): void {
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
}
window.addEventListener('resize', resizeCanvas);
resizeCanvas();

// ---------------------------------------------------------------------------
// Hidden video element for MediaPipe
// ---------------------------------------------------------------------------

const videoEl = document.createElement('video');
// Semi-transparent mirrored camera feed as the game background (blueprint
// requirement). Hidden (opacity 0) until the stream starts; dimmed via filter
// so gameplay sprites stay readable on top.
videoEl.style.cssText = [
  'position:fixed', 'inset:0', 'width:100%', 'height:100%',
  'object-fit:cover', 'transform:scaleX(-1)', 'z-index:1',
  'opacity:0', 'transition:opacity .5s ease',
  // CSS filter on a fullscreen video forces an offscreen compositing pass on
  // EVERY frame — measurable jank on phone GPUs. Mobile skips the filter and
  // dims via a lower reveal opacity instead (perf.mobile.videoOpacity).
  ...(IS_MOBILE ? [] : ['filter:brightness(.55) saturate(.85)']),
  'pointer-events:none',
].join(';');
document.body.appendChild(videoEl);

// ---------------------------------------------------------------------------
// DOM UI panels (HTML over canvas — accessible, keyboard-friendly)
// ---------------------------------------------------------------------------

function panel(id: string, html: string): HTMLElement {
  const el = document.createElement('div');
  el.id = id;
  el.innerHTML = html;
  el.style.cssText = [
    'position:fixed', 'inset:0', 'display:flex', 'flex-direction:column',
    'align-items:center', 'justify-content:center',
    'background:rgba(10,10,30,0.6)', 'color:#fff',
    "font-family:'Space Grotesk',system-ui,sans-serif", 'z-index:20',
  ].join(';');
  mount!.appendChild(el);
  return el;
}

// ---- Start Screen ----------------------------------------------------------

const startPanel = panel('screen-start', `
  <h1 style="margin:0">Fruit Slasher</h1>
  <p style="font-size:1.1rem;color:#aaa;margin:.5rem 0 2rem">Slice fruit with your hand. Avoid bombs.</p>
  <p style="font-size:.9rem;color:#888;max-width:380px;text-align:center;margin-bottom:2rem">
    This game uses your front camera to track hand movements.
    No video is recorded or transmitted. Camera access is required to play.
  </p>
  <button id="btn-start" style="padding:16px 48px;font-size:1.4rem;font-weight:700;background:#e94560;color:#fff;border:none;border-radius:12px;cursor:pointer">
    Start
  </button>
  <p style="font-size:.75rem;color:#666;margin-top:1.5rem">Or use your mouse pointer to play without camera</p>
`);

// ---- Mode Select -----------------------------------------------------------

const modePanel = panel('screen-mode', `
  <h2 style="font-size:2rem;margin:0 0 2rem">Choose Mode</h2>
  <div style="display:flex;gap:2rem;flex-wrap:wrap;justify-content:center">
    <button id="btn-classic" style="padding:24px 40px;font-size:1.2rem;font-weight:700;background:#ff7043;color:#fff;border:none;border-radius:12px;cursor:pointer;min-width:180px">
      Classic<br><small style="font-weight:400;font-size:.85rem">3 lives · bombs · no time limit</small>
    </button>
    <button id="btn-zen" style="padding:24px 40px;font-size:1.2rem;font-weight:700;background:#4fc3f7;color:#0a0a1e;border:none;border-radius:12px;cursor:pointer;min-width:180px">
      Zen<br><small style="font-weight:400;font-size:.85rem">60 seconds · no bombs · chill</small>
    </button>
  </div>
  <button id="btn-back-mode" style="margin-top:2rem;padding:12px 28px;min-height:44px;font-size:1rem;background:transparent;color:#888;border:1px solid #444;border-radius:8px;cursor:pointer">Back</button>
`);
modePanel.style.display = 'none';

// ---- READY Gate -------------------------------------------------------------
// Shown between mode select (or retry) and PLAYING. The game must NOT start
// until camera + tracker are live and a hand is confirmed in frame — players
// were burning all 3 lives against an input source still initialising.

const readyPanel = panel('screen-ready', `
  <h2 style="font-size:2rem;margin:0 0 1rem">${STRINGS.ready.headline}</h2>
  <div id="ready-msg" style="font-size:1.15rem;color:#ccc;min-height:1.6em;text-align:center;padding:0 1rem"></div>
  <div id="ready-count" style="font-size:6rem;font-weight:700;min-height:1.2em;line-height:1.2;color:#39FF14"></div>
  <button id="btn-ready-skip" style="padding:12px 28px;min-height:44px;font-size:1rem;background:transparent;color:#aaa;border:1px solid #555;border-radius:8px;cursor:pointer;margin-bottom:.75rem">${STRINGS.ready.skipButton}</button>
  <button id="btn-ready-back" style="padding:10px 24px;min-height:44px;font-size:.9rem;background:transparent;color:#777;border:none;cursor:pointer;text-decoration:underline">${STRINGS.ready.backButton}</button>
`);
readyPanel.style.display = 'none';
const readyMsgEl = readyPanel.querySelector<HTMLElement>('#ready-msg')!;
const readyCountEl = readyPanel.querySelector<HTMLElement>('#ready-count')!;
const readySkipBtn = readyPanel.querySelector<HTMLElement>('#btn-ready-skip')!;

// ---- HUD -------------------------------------------------------------------

const hudPanel = document.createElement('div');
hudPanel.id = 'hud';
// Safe-area maxes keep the HUD clear of phone notches/camera cutouts
// (viewport-fit=cover is set in index.html so the env() insets are live).
hudPanel.style.cssText = "position:fixed;top:0;left:0;right:0;padding:max(12px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 12px max(20px, env(safe-area-inset-left));display:flex;justify-content:space-between;align-items:flex-start;pointer-events:none;z-index:15;font-family:'Space Grotesk',system-ui,sans-serif;color:#fff;display:none;";
hudPanel.innerHTML = `
  <div id="hud-score" style="font-size:2rem;font-weight:700;text-shadow:0 0 8px rgba(0,0,0,.8)">0</div>
  <div id="hud-center" style="text-align:center">
    <div id="hud-combo" style="font-size:1.1rem;color:#ffe800;text-shadow:0 0 6px rgba(0,0,0,.8)"></div>
    <div id="hud-timer" style="font-size:1.5rem;font-weight:700;text-shadow:0 0 8px rgba(0,0,0,.8)"></div>
  </div>
  <div id="hud-lives" style="font-size:1.5rem;text-shadow:0 0 8px rgba(0,0,0,.8)">♥♥♥</div>
`;
document.body.appendChild(hudPanel);

// Cache HUD element refs once — avoids getElementById on every render frame.
const hudScoreEl = hudPanel.querySelector<HTMLElement>('#hud-score')!;
const hudLivesEl = hudPanel.querySelector<HTMLElement>('#hud-lives')!;
const hudComboEl = hudPanel.querySelector<HTMLElement>('#hud-combo')!;
const hudTimerEl = hudPanel.querySelector<HTMLElement>('#hud-timer')!;

// ---- Pause Panel -----------------------------------------------------------

const pausePanel = panel('screen-pause', `
  <h2 style="font-size:2.5rem;margin:0 0 .5rem">Paused</h2>
  <div id="pause-note" style="font-size:1rem;color:#ffb84d;min-height:1.3em;margin-bottom:1.5rem;text-align:center;padding:0 1rem"></div>
  <button id="btn-resume" style="padding:16px 48px;font-size:1.2rem;font-weight:700;background:#4caf50;color:#fff;border:none;border-radius:12px;cursor:pointer;margin-bottom:1rem">Resume</button>
  <button id="btn-quit-pause" style="padding:12px 36px;font-size:1rem;background:transparent;color:#888;border:1px solid #444;border-radius:8px;cursor:pointer">Quit to Menu</button>
`);
pausePanel.style.display = 'none';
/** Sub-line under "Paused" — set when the game auto-pauses on tracking loss. */
const pauseNoteEl = pausePanel.querySelector<HTMLElement>('#pause-note')!;

// ---- Game Over Panel -------------------------------------------------------

const gameOverPanel = panel('screen-gameover', `
  <h2 id="go-title" style="font-size:2.5rem;margin:0 0 .5rem;color:#e94560">Game Over</h2>
  <div id="go-score" style="font-size:4rem;font-weight:700;margin:.5rem 0"></div>
  <div id="go-best" style="font-size:1rem;color:#ffd700;margin-bottom:2rem"></div>
  <div style="display:flex;gap:1rem;flex-wrap:wrap;justify-content:center">
    <button id="btn-retry" style="padding:14px 36px;font-size:1.1rem;font-weight:700;background:#e94560;color:#fff;border:none;border-radius:10px;cursor:pointer">Retry</button>
    <button id="btn-share" style="padding:14px 36px;font-size:1.1rem;font-weight:700;background:#ff9800;color:#fff;border:none;border-radius:10px;cursor:pointer">Share Score</button>
    <button id="btn-menu-go" style="padding:14px 36px;font-size:1.1rem;background:transparent;color:#888;border:1px solid #444;border-radius:10px;cursor:pointer">Menu</button>
  </div>
  <div id="go-name-entry" style="margin-top:2rem;display:flex;gap:.5rem;align-items:center;display:none">
    <input id="go-name-input" maxlength="32" placeholder="Enter your name" style="padding:10px 14px;min-height:44px;font-size:1rem;border-radius:8px;border:1px solid #555;background:#1a1a2e;color:#fff;width:200px">
    <button id="btn-save-score" style="padding:10px 20px;min-height:44px;font-size:1rem;font-weight:700;background:#4caf50;color:#fff;border:none;border-radius:8px;cursor:pointer">Save</button>
  </div>
`);
gameOverPanel.style.display = 'none';

// ---- Combo popup -----------------------------------------------------------

const comboPopupEl = document.createElement('div');
comboPopupEl.style.cssText = [
  'position:fixed', 'left:50%', 'top:35%', 'transform:translate(-50%,-50%)',
  'font-size:2rem', 'font-weight:700', 'color:#ffe800',
  'text-shadow:0 0 12px rgba(255,200,0,.6)', 'pointer-events:none',
  'z-index:16', 'transition:opacity .15s', 'opacity:0',
].join(';');
document.body.appendChild(comboPopupEl);

// ---------------------------------------------------------------------------
// Game systems (instantiated once; reset on each new game)
// ---------------------------------------------------------------------------

const stateMachine = new StateMachine();
const gameLoop = new GameLoop(onFrame);
const dwell = new DwellClickManager();

// Tracking
const smoother0 = new OneEuroFilter2D(CONFIG.slash.smoothing);
const smoother1 = new OneEuroFilter2D(CONFIG.slash.smoothing);
const interp = new PointerInterpolator();

// Gameplay (reset per session)
let spawner = new Spawner();
let scoreManager = new ScoreManager(CONFIG.game.lives, 'classic');
let powerUpMgr = new PowerUpManager();
let frenzyMgr = new FrenzyManager('classic');
let obstacleMgr = new ObstacleManager();
let bladeTrail = new BladeTrail();
let comboPopupMgr = new ComboPopupManager();

// FX
const hitStop = new HitStop();
const screenShake = new ScreenShake();
const slowMo = new SlowMo(REDUCED_MOTION);
const particles = new ParticleSystem();
const leaderboard = new LocalLeaderboard();

// Audio — native HTMLAudioElement player (clone-on-play for overlap,
// playbackRate + preservesPitch=false for the combo pitch-up effect).
const audioMgr = new AudioManager(createNativeAudioPlayer());

// ---------------------------------------------------------------------------
// Live entity collections
// ---------------------------------------------------------------------------

let fruits: Fruit[] = [];
let bombs: Bomb[] = [];
let powerUps: PowerUp[] = [];
/** Loaded SVG texture registry — render falls back to debug circles while null. */
let textures: TextureRegistry | null = null;
/** Shared no-op container — sprites are drawn directly to the 2D canvas. */
const NOOP_CONTAINER = {
  addChild(_c: unknown) { /* canvas-drawn — no scene graph */ },
  removeChild(_c: unknown) { /* canvas-drawn — no scene graph */ },
};
/** Render-only view of Fruit's internal split halves. */
interface SplitHalvesView {
  halfA: { x: number; y: number };
  halfB: { x: number; y: number };
  splitTime: number;
}
let gameMode: GameMode = 'classic';
let zenTimeRemaining = 0;
/** Real elapsed play time (ms) this session — drives the concurrent-fruit ramp. */
let gameElapsedMs = 0;
/** Tracks the active pointer source so gesture state resets on hand↔mouse switches. */
let lastSourceWasHand = false;
/**
 * Last frame time with a fresh hand pointer THIS run (0 = hand never seen).
 * Drives the tracking-loss auto-pause — only armed once hand input was real.
 */
let lastHandSeenMs = 0;
/**
 * Timestamp (ms) of the previous processTrackingResult call — used to compute
 * dtMs for the one-euro derivative and for hand-identity gating. Reset to 0 on
 * each fresh session so the first frame uses CONFIG.tracking.expectedIntervalMs
 * as a conservative dt rather than a giant gap from the previous session.
 */
let lastProcessTsMs = 0;
let cameraStream: MediaStream | null = null;
let mousePointer: { x: number; y: number } | null = null;
let activePointers: HandPointer[] = [];

// ---------------------------------------------------------------------------
// Mouse + touch fallback — Pointer Events cover both. On phones a dragged
// finger IS the blade (touch-action:none in index.html stops the browser
// from stealing the gesture for scroll/zoom/refresh).
// ---------------------------------------------------------------------------

canvas.style.pointerEvents = 'auto';

/**
 * Last wall-clock (performance.now) of ANY mouse/touch movement. The hand-loss
 * auto-pause uses this to tell "player switched to touch" from "player gone".
 */
let lastPointerActivityMs = 0;

// Window-level listeners keep working regardless of which layer (canvas,
// panels) is under the pointer — the old canvas-only mousemove went silent
// whenever the canvas had pointer-events:none.
const onPointerInput = (e: PointerEvent): void => {
  mousePointer = { x: e.clientX, y: e.clientY };
  lastPointerActivityMs = performance.now();
};
window.addEventListener('pointermove', onPointerInput);
window.addEventListener('pointerdown', onPointerInput);

// A lifted finger must not leave a stale hover point behind (hover is
// meaningful for a mouse, meaningless for touch).
const onPointerEnd = (e: PointerEvent): void => {
  if (e.pointerType !== 'mouse') mousePointer = null;
};
window.addEventListener('pointerup', onPointerEnd);
window.addEventListener('pointercancel', onPointerEnd);
document.documentElement.addEventListener('mouseleave', () => {
  mousePointer = null;
});

// Allow click-through for UI buttons behind the canvas in non-playing states.
// During PLAYING the OS mouse arrow is hidden too — a static arrow parked
// over the playfield reads as visual noise; mouse players see their motion
// as the blade trail, hand players see no stray cursor at all.
stateMachine.onTransition((next) => {
  canvas.style.pointerEvents = next === 'PLAYING' ? 'auto' : 'none';
  canvas.style.cursor = next === 'PLAYING' ? 'none' : '';
});

// ---------------------------------------------------------------------------
// State machine wiring
// ---------------------------------------------------------------------------

stateMachine.onTransition((next, prev) => {
  // Screen visibility
  startPanel.style.display = next === 'MENU' ? 'flex' : 'none';
  modePanel.style.display = next === 'MODESELECT' ? 'flex' : 'none';
  readyPanel.style.display = next === 'READY' ? 'flex' : 'none';
  hudPanel.style.display = next === 'PLAYING' ? 'flex' : 'none';
  pausePanel.style.display = next === 'PAUSED' ? 'flex' : 'none';
  gameOverPanel.style.display = next === 'GAMEOVER' ? 'flex' : 'none';

  if (next === 'READY') {
    // Kick the camera init (no-op if already live) and run the gate:
    // camera ready → hand confirmed → countdown → PLAYING.
    initCameraAndTracker();
    void runReadyFlow();
  }
  if (prev === 'PAUSED') {
    pauseNoteEl.textContent = ''; // clear any auto-pause note for next time
  }
  if (next === 'PLAYING' && prev !== 'PAUSED') {
    startNewGame();
  }
  if (next !== 'PLAYING') {
    gameLoop.stop();
    resetSlashState();
  }
  if (next !== 'PLAYING' && next !== 'PAUSED') {
    // Clear the field — but NOT on pause, so resume keeps entities in place.
    fruits = [];
    bombs = [];
    powerUps = [];
    particles.clear();
  }
  if (next === 'PLAYING') {
    gameLoop.start();
  }
  if (next === 'GAMEOVER') {
    showGameOver();
  }

  // Rebuild dwell targets after a layout tick (DOM must be visible first).
  // Only button IDs relevant to the current state are registered so that
  // stray dwell events cannot activate off-screen buttons.
  requestAnimationFrame(() => {
    const targets: import('./input/dwellClick.js').DwellTargetRect[] = [];
    const ids =
      next === 'MENU'      ? ['btn-start'] :
      next === 'MODESELECT'? ['btn-classic', 'btn-zen', 'btn-back-mode'] :
      next === 'PAUSED'    ? ['btn-resume', 'btn-quit-pause'] :
      next === 'GAMEOVER'  ? ['btn-retry', 'btn-menu-go'] :
      []; // READY + PLAYING: no dwell targets (InputRouter disables dwell)
    for (const id of ids) {
      const el = document.getElementById(id);
      if (!el) continue;
      const r = el.getBoundingClientRect();
      targets.push({ id, x: r.left, y: r.top, width: r.width, height: r.height });
    }
    dwell.setTargets(targets);
  });
});

// ---------------------------------------------------------------------------
// Button wiring
// ---------------------------------------------------------------------------

document.getElementById('btn-start')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  stateMachine.transition('MODESELECT');
});

document.getElementById('btn-classic')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  gameMode = 'classic';
  stateMachine.transition('READY');
});

document.getElementById('btn-zen')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  gameMode = 'zen';
  stateMachine.transition('READY');
});

document.getElementById('btn-back-mode')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  stateMachine.transition('MENU');
});

document.getElementById('btn-resume')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  stateMachine.transition('PLAYING');
});

document.getElementById('btn-quit-pause')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  stateMachine.transition('MENU');
});

document.getElementById('btn-retry')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  // Straight back through the readiness gate, same mode. The old detour
  // (MENU → MODESELECT → PLAYING in 100ms) STOPPED and restarted the camera,
  // so every retry started blind and players lost lives instantly.
  stateMachine.transition('READY');
});

document.getElementById('btn-ready-skip')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  readySkip = true; // jump the hand wait — countdown starts in touch/mouse mode
});

document.getElementById('btn-ready-back')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  if (stateMachine.state === 'READY') stateMachine.transition('MENU');
});

document.getElementById('btn-menu-go')?.addEventListener('click', () => {
  audioMgr.onUiClick();
  stateMachine.transition('MENU');
});

document.getElementById('btn-share')?.addEventListener('click', () => {
  const shareCanvas = document.createElement('canvas');
  const score = scoreManager.state.score;
  const rank = leaderboard.personalBest(gameMode) === score ? 1 : null;
  const dataUrl = renderShareCard(shareCanvas, { score, mode: gameMode, rank });
  void shareOrDownload(dataUrl, score);
});

document.getElementById('btn-save-score')?.addEventListener('click', () => {
  const input = document.getElementById('go-name-input') as HTMLInputElement | null;
  const name = (input?.value.trim() ?? '').slice(0, 32) || 'Anonymous';
  leaderboard.submit({ name, score: scoreManager.state.score, mode: gameMode });
  const nameEntry = document.getElementById('go-name-entry');
  if (nameEntry) nameEntry.style.display = 'none';
  audioMgr.onUiClick();
});

// Pause on Escape key during gameplay.
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && stateMachine.state === 'PLAYING') {
    stateMachine.transition('PAUSED');
  }
});

// ---------------------------------------------------------------------------
// New game init
// ---------------------------------------------------------------------------

function startNewGame(): void {
  fruits = [];
  bombs = [];
  powerUps = [];
  particles.clear();
  bladeTrail = new BladeTrail();
  comboPopupMgr = new ComboPopupManager();
  spawner = new Spawner();
  scoreManager = new ScoreManager(
    gameMode === 'classic' ? CONFIG.game.lives : 0,
    gameMode,
  );
  powerUpMgr = new PowerUpManager();
  frenzyMgr = new FrenzyManager(gameMode);
  obstacleMgr = new ObstacleManager();
  hitStop.update(9999); // reset by overflowing
  screenShake.update(9999);
  slowMo.reset();
  zenTimeRemaining = gameMode === 'zen' ? CONFIG.game.zenModeDuration * 1000 : 0;
  gameElapsedMs = 0;
  lastHandSeenMs = 0;
  // Reset hand-identity slot state and dt baseline so the first frame of the
  // new session uses expectedIntervalMs as dt (not a gap from the old session).
  resetHandIdentity();
  lastProcessTsMs = 0;
}

// ---------------------------------------------------------------------------
// Game over display
// ---------------------------------------------------------------------------

function showGameOver(): void {
  const s = scoreManager.state;
  const titleEl = document.getElementById('go-title');
  const scoreEl = document.getElementById('go-score');
  const bestEl = document.getElementById('go-best');
  const nameEntry = document.getElementById('go-name-entry');

  if (titleEl) titleEl.textContent = gameMode === 'zen' ? 'Time\'s Up!' : 'Game Over';
  if (scoreEl) scoreEl.textContent = String(s.score);

  const best = leaderboard.personalBest(gameMode);
  const isNewBest = best === null || s.score > best;
  if (bestEl) {
    bestEl.textContent = isNewBest
      ? '★ New personal best!'
      : best !== null ? `Personal best: ${best}` : '';
  }

  // Show name entry if it's a top-10 candidate.
  const entries = leaderboard.getEntries().filter((e) => e.mode === gameMode);
  const wouldEnter = entries.length < 10 || (entries[entries.length - 1]?.score ?? 0) < s.score;
  if (nameEntry) {
    nameEntry.style.display = wouldEnter ? 'flex' : 'none';
  }

  audioMgr.onUiClick(); // game-over sound
}

// ---------------------------------------------------------------------------
// HUD update
// ---------------------------------------------------------------------------

function updateHUD(): void {
  const s = scoreManager.state;

  // Use cached refs — avoids getElementById on every 60fps render frame.
  hudScoreEl.textContent = String(s.score);
  hudLivesEl.textContent = gameMode === 'classic'
    ? '♥'.repeat(Math.max(0, s.lives))
    : '';
  hudComboEl.textContent = s.combo >= 2 ? `${s.combo}x combo` : '';
  hudTimerEl.textContent = gameMode === 'zen'
    ? `${Math.ceil(zenTimeRemaining / 1000)}s`
    : '';
}

// ---------------------------------------------------------------------------
// Combo popup render
// ---------------------------------------------------------------------------

function updateComboPopup(nowMs: number): void {
  const ev = comboPopupMgr.active;
  comboPopupMgr.tick(nowMs);
  if (ev) {
    const age = nowMs - ev.createdAt;
    const alpha = Math.max(0, 1 - age / CONFIG.combo.popupFadeMs);
    comboPopupEl.textContent = ev.label;
    comboPopupEl.style.opacity = String(alpha);
  } else {
    comboPopupEl.style.opacity = '0';
  }
}

// ---------------------------------------------------------------------------
// Slash collision
// ---------------------------------------------------------------------------

function handleSlash(seg: SlashSegment): void {
  if (stateMachine.state !== 'PLAYING') return;

  // Bombs first — a slash crossing a bomb ends the run immediately (Classic).
  for (const bomb of bombs) {
    if (!segmentHitsCircle(seg, bomb.x, bomb.y, bomb.hitRadius)) continue;
    if (DEV) debugLog('HIT bomb — game over');
    audioMgr.onBomb();
    if (!REDUCED_MOTION) {
      screenShake.trigger(
        CONFIG.feel.screenShakeIntensity * 3,
        CONFIG.feel.screenShakeDuration * 3,
      );
    }
    scoreManager.hitBomb();
    stateMachine.transition('GAMEOVER');
    return;
  }

  // Power-ups — slash to collect and activate (generous hitbox, they're rewards).
  const collected: PowerUp[] = [];
  for (const pu of powerUps) {
    if (!segmentHitsCircle(seg, pu.x, pu.y, pu.hitRadius * CONFIG.slash.fruitHitboxScale)) continue;
    if (DEV) debugLog(`HIT powerup ${pu.type}`);
    powerUpMgr.activate(pu.type);
    audioMgr.onPowerUp();
    collected.push(pu);
  }
  if (collected.length > 0) {
    const collectedSet = new Set(collected);
    for (const pu of collected) destroyPowerUp(pu, NOOP_CONTAINER);
    powerUps = powerUps.filter((p) => !collectedSet.has(p));
  }

  let hitAny = false;

  for (const fruit of fruits) {
    if (fruit.state !== 'whole') continue;
    // Generous hitbox: collision radius matches (slightly exceeds) the 1.7×
    // sprite visual — a slash that visibly crosses the fruit must connect.
    if (!segmentHitsCircle(seg, fruit.x, fruit.y, fruit.hitRadius * CONFIG.slash.fruitHitboxScale)) continue;

    hitAny = true;
    if (DEV) debugLog(`HIT ${fruit.type}${fruit.isCritical ? ' (critical)' : ''}`);
    sliceFruit(fruit, { x: seg.to.x - seg.from.x, y: seg.to.y - seg.from.y }, {
      addChild() { /* sprite managed by Fruit.ts stub */ },
      removeChild() { /* sprite managed by Fruit.ts stub */ },
    });

    const isCritical = fruit.isCritical;
    const isDouble = powerUpMgr.doubleActive;
    scoreManager.slashFruit(fruit.type, isCritical, isDouble);
    bladeTrail.setCombo(scoreManager.state.combo);

    const popupCombo = scoreManager.state.combo;
    comboPopupMgr.onCombo(popupCombo, performance.now());
    if (popupCombo >= 2) audioMgr.onComboMilestone();

    audioMgr.onSlash(scoreManager.state.combo);
    audioMgr.onSplat();

    // FX
    hitStop.trigger(CONFIG.feel.hitstopMs);
    screenShake.trigger(CONFIG.feel.screenShakeIntensity, CONFIG.feel.screenShakeDuration);
    if (isCritical && !REDUCED_MOTION) slowMo.triggerCritical();

    // Juice particles (scaled down on mobile — fill-rate is precious there)
    const juiceColor = CONFIG.juiceColors[fruit.type];
    const [minC, maxC] = CONFIG.particles.countRange;
    const count = (minC + Math.floor(Math.random() * (maxC - minC + 1))) *
      (isCritical ? CONFIG.particles.criticalCountMultiplier : 1);
    particles.emit(fruit.x, fruit.y, juiceColor, Math.max(1, Math.round(count * PARTICLE_SCALE)));
  }

  if (!hitAny && DEV) {
    // Miss diagnostics: report the nearest entity and the gap to its hitbox —
    // this is the data that explains "I clearly slashed it but nothing happened".
    let bestGap = Infinity;
    let bestName = '';
    for (const fruit of fruits) {
      if (fruit.state !== 'whole') continue;
      const gap =
        distPointToSegment({ x: fruit.x, y: fruit.y }, seg.from, seg.to) -
        fruit.hitRadius * CONFIG.slash.fruitHitboxScale;
      if (gap < bestGap) { bestGap = gap; bestName = fruit.type; }
    }
    for (const bomb of bombs) {
      const gap = distPointToSegment({ x: bomb.x, y: bomb.y }, seg.from, seg.to) - bomb.hitRadius;
      if (gap < bestGap) { bestGap = gap; bestName = 'bomb'; }
    }
    for (const pu of powerUps) {
      const gap =
        distPointToSegment({ x: pu.x, y: pu.y }, seg.from, seg.to) -
        pu.hitRadius * CONFIG.slash.fruitHitboxScale;
      if (gap < bestGap) { bestGap = gap; bestName = `pu:${pu.type}`; }
    }
    if (bestName) {
      debugLog(`MISS — ${bestName} ${Math.round(bestGap)}px outside hitbox`);
    } else {
      debugLog('MISS — no slashable entity on screen');
    }
  }
}

// ---------------------------------------------------------------------------
// Main render/game-tick frame
// ---------------------------------------------------------------------------

function onFrame(deltaMs: number, nowMs: number): void {
  const state = stateMachine.state;
  if (state !== 'PLAYING') return;
  renderFrameCount++;

  // Raw delta from rAF — used for FX timing (shake/slowmo tick on real time).
  hitStop.update(deltaMs);
  screenShake.update(deltaMs);
  slowMo.tick(deltaMs);

  // Scaled delta for physics and game logic.
  const dt = hitStop.active ? 0 : (deltaMs / 1000) * slowMo.timeScale;

  // Zen timer
  if (gameMode === 'zen') {
    zenTimeRemaining -= deltaMs * slowMo.timeScale;
    if (zenTimeRemaining <= 0) {
      zenTimeRemaining = 0;
      stateMachine.transition('GAMEOVER');
      return;
    }
  }

  // Power-up + obstacle timers
  powerUpMgr.tick(deltaMs);
  obstacleMgr.tick(deltaMs);

  // Frenzy
  const frenzyTrigger = frenzyMgr.tick(deltaMs);
  if (frenzyTrigger) {
    audioMgr.onFrenzy();
    if (!REDUCED_MOTION) slowMo.triggerFrenzy();
    screenShake.trigger(CONFIG.feel.screenShakeIntensity * 2, CONFIG.feel.screenShakeDuration * 2);
  }

  const isClassic = gameMode === 'classic';

  // Spawner — normal cadence (isFrenzy=false so the accumulator runs at base rate).
  // Frenzy burst is handled separately below via frenzyTrigger.fruitCount.
  const requests = spawner.tick(deltaMs, {
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  }, false, isClassic);

  // Concurrency ramp: start gentle — only CONFIG.game.concurrentStart fruit
  // in the air at once, +1 allowed every CONFIG.game.concurrentRampMs.
  // Fruit requests beyond the cap are dropped; the spawner re-emits on its
  // next interval, so pacing self-corrects.
  gameElapsedMs += deltaMs;
  const maxConcurrent =
    CONFIG.game.concurrentStart +
    Math.floor(gameElapsedMs / CONFIG.game.concurrentRampMs);
  let airborne = 0;
  for (const f of fruits) {
    if (f.state === 'whole') airborne++;
  }

  const containerStub = {
    addChild(_c: unknown) { /* PixiJS integration point */ },
    removeChild(_c: unknown) { /* PixiJS integration point */ },
  };

  for (const req of requests) {
    if (req.kind === 'fruit' && req.fruitType) {
      if (airborne >= maxConcurrent) continue; // over cap — drop this request
      airborne++;
      const f = createFruit(
        req.fruitType,
        { x: req.x, y: req.y },
        { x: req.vx, y: req.vy },
        containerStub,
        req.isCritical,
      );
      fruits.push(f);
    } else if (req.kind === 'bomb') {
      bombs.push(createBomb(
        { x: req.x, y: req.y },
        { x: req.vx, y: req.vy },
        NOOP_CONTAINER,
      ));
    } else if (req.kind === 'powerup') {
      const types: PowerUpType[] = ['freeze', 'double', 'magnet'];
      const type = types[Math.floor(Math.random() * types.length)] ?? 'freeze';
      powerUps.push(createPowerUp(
        type,
        { x: req.x, y: req.y },
        { x: req.vx, y: req.vy },
        NOOP_CONTAINER,
      ));
    }
  }

  // Frenzy burst — spawn fruitCount fruits immediately on the trigger frame.
  // Uses spawnBurst() which bypasses the accumulator, so the normal cadence
  // is unaffected. Bombs are excluded from frenzy bursts (isClassic=false).
  if (frenzyTrigger) {
    // Frenzy respects the concurrency ramp too — early-game frenzy waves are
    // small, late-game waves flood the screen as the cap grows.
    const burstBudget = Math.max(0, maxConcurrent - airborne);
    const burstReqs = spawner.spawnBurst(Math.min(frenzyTrigger.fruitCount, burstBudget), {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    }, false /* no bombs in frenzy burst */);
    for (const req of burstReqs) {
      if (req.kind === 'fruit' && req.fruitType) {
        const f = createFruit(
          req.fruitType,
          { x: req.x, y: req.y },
          { x: req.vx, y: req.vy },
          containerStub,
          req.isCritical,
        );
        fruits.push(f);
      }
    }
  }

  // Physics update + offscreen removal
  const velocityScale = powerUpMgr.velocityScale;
  const toRemove: Fruit[] = [];

  const magnetTarget = powerUpMgr.magnetActive ? activePointers[0] ?? null : null;

  for (const fruit of fruits) {
    // Apply velocity scale from freeze power-up
    fruit.vx *= velocityScale < 1 ? (1 - (1 - velocityScale) * dt * 3) : 1;
    // Magnet power-up: pull fruit toward the primary pointer.
    if (magnetTarget && fruit.state === 'whole') {
      const mdx = magnetTarget.x - fruit.x;
      const mdy = magnetTarget.y - fruit.y;
      const distSq = mdx * mdx + mdy * mdy;
      const radius = CONFIG.powerup.magnetRadius;
      if (distSq < radius * radius && distSq > 1) {
        const dist = Math.sqrt(distSq);
        fruit.vx += (mdx / dist) * CONFIG.powerup.magnetPullSpeed * dt;
        fruit.vy += (mdy / dist) * CONFIG.powerup.magnetPullSpeed * dt;
      }
    }
    const exited = updateFruit(fruit, dt, canvas.width, canvas.height);
    if (exited) {
      if (fruit.state !== 'splitting') {
        scoreManager.missedFruit();
      }
      toRemove.push(fruit);
    }
  }

  if (toRemove.length > 0) {
    const removeSet = new Set(toRemove);
    for (const f of toRemove) {
      destroyFruit(f, containerStub);
    }
    fruits = fruits.filter((fr) => !removeSet.has(fr));
  }

  // Bomb physics — same parabolic integration; escaped bombs carry no penalty.
  if (bombs.length > 0) {
    const goneBombs: Bomb[] = [];
    for (const bomb of bombs) {
      if (updateBomb(bomb, dt, canvas.width, canvas.height)) goneBombs.push(bomb);
    }
    if (goneBombs.length > 0) {
      const goneSet = new Set(goneBombs);
      for (const bomb of goneBombs) destroyBomb(bomb, NOOP_CONTAINER);
      bombs = bombs.filter((b) => !goneSet.has(b));
    }
  }

  // Power-up physics — escaping uncollected is the player's loss, no penalty.
  if (powerUps.length > 0) {
    const gonePUs: PowerUp[] = [];
    for (const pu of powerUps) {
      if (updatePowerUp(pu, dt, canvas.width, canvas.height)) gonePUs.push(pu);
    }
    if (gonePUs.length > 0) {
      const goneSet = new Set(gonePUs);
      for (const pu of gonePUs) destroyPowerUp(pu, NOOP_CONTAINER);
      powerUps = powerUps.filter((p) => !goneSet.has(p));
    }
  }

  // Pointer source priority: HAND TRACKING FIRST — it is the game's core
  // input. The mouse only drives the blade while NO fresh hand pointer
  // exists (camera denied, hand out of frame, tracking lost). Previously the
  // mouse overwrote hand pointers permanently after a single mousemove.
  const handIsFresh =
    activePointers.length > 0 &&
    activePointers.some((p) => nowMs - p.timestamp < CONFIG.tracking.handLostDebounceMs);

  let gesturePointers: HandPointer[] = activePointers;
  if (handIsFresh) {
    // Hand mode — feed the primary hand's trail at RENDER rate from the
    // interpolator. Tracking samples arrive at only ~30fps; pushing them raw
    // makes the trail (and perceived responsiveness) choppy vs mouse.
    // getCoasted keeps the trail sweeping through brief detection dropouts.
    const ip = interp.getCoasted(nowMs, COAST_OPTS);
    if (ip) bladeTrail.push(ip.x, ip.y, nowMs);
  } else if (
    mousePointer &&
    nowMs - lastPointerActivityMs < CONFIG.tracking.mouseIdleHideMs
  ) {
    // Only a RECENTLY MOVED mouse may drive the blade. An idle mouse parked
    // on screen used to hijack the blade the instant hand tracking blipped —
    // the cursor "jumped" to a stale position and movement looked chaotic.
    const mp: HandPointer = {
      handIndex: 0,
      x: mousePointer.x,
      y: mousePointer.y,
      rawX: mousePointer.x,
      rawY: mousePointer.y,
      timestamp: nowMs,
    };
    gesturePointers = [mp];
    bladeTrail.push(mp.x, mp.y, nowMs);
  }

  // Reset gesture state when the pointer SOURCE switches (hand ↔ mouse) —
  // otherwise the jump between the two positions registers as one giant
  // phantom slash.
  if (handIsFresh !== lastSourceWasHand) {
    resetSlashState();
    lastSourceWasHand = handIsFresh;
  }


  // Tracking-loss auto-pause: hand input was active this run but has been
  // gone a while AND no mouse/touch is substituting — pause instead of
  // letting fruit fall through a blade that no longer exists.
  if (handIsFresh) lastHandSeenMs = nowMs;
  if (
    lastHandSeenMs > 0 &&
    nowMs - lastHandSeenMs > CONFIG.tracking.autoPauseAfterMs &&
    performance.now() - lastPointerActivityMs > CONFIG.tracking.autoPauseAfterMs
  ) {
    pauseNoteEl.textContent = STRINGS.hints.trackingLost;
    stateMachine.transition('PAUSED');
    return;
  }

  // Process slash gestures
  if (InputRouter.slashEnabled(state)) {
    const segments = gestureProcessFrame(gesturePointers, true);
    for (const seg of segments) {
      handleSlash(seg);
    }
  }

  // Blade trail maintenance
  bladeTrail.prune(nowMs);

  // Particles
  particles.update(deltaMs, CONFIG.fruit.gravity * 0.3);

  // Game-over check (Classic only — Zen ends by timer above)
  if (scoreManager.isGameOver) {
    stateMachine.transition('GAMEOVER');
    return;
  }

  // Render
  render(nowMs);

  // HUD + combo popup
  updateHUD();
  updateComboPopup(nowMs);
}

// ---------------------------------------------------------------------------
// Canvas 2D render (overlay — pointer, blade trail, particles)
// ---------------------------------------------------------------------------

function render(nowMs: number): void {
  ctx2d.clearRect(0, 0, canvas.width, canvas.height);

  // Screen shake offset
  const shake = screenShake.offset;

  ctx2d.save();
  ctx2d.translate(shake.x, shake.y);

  // Blade trail — clean solid stroke (no glow), drawn as QUADRATIC CURVES
  // through segment midpoints: hand paths are curved but samples arrive as
  // a polyline, and straight lineTo segments leave visible corners at every
  // tracking sample ("patah-patah"). Midpoint smoothing rounds them all.
  // Width tapers from maxWidth (newest) toward the tail with a 2px floor.
  const trailPts = bladeTrail.points;
  if (trailPts.length >= 2) {
    ctx2d.save();
    ctx2d.lineCap = 'round';
    ctx2d.lineJoin = 'round';
    ctx2d.strokeStyle = bladeTrail.color;
    for (let i = 1; i < trailPts.length; i++) {
      const prev = trailPts[i - 1];
      const curr = trailPts[i];
      const next = trailPts[i + 1] ?? null;
      if (!prev || !curr) continue;
      const alpha = Math.max(0, 1 - (nowMs - curr.timestamp) / bladeTrail.fadeMs);
      if (alpha <= 0) continue;
      const t = i / trailPts.length;

      // Segment runs midpoint(prev,curr) → midpoint(curr,next) with curr as
      // the curve control point; the final segment ends at curr itself.
      const m1x = (prev.x + curr.x) / 2;
      const m1y = (prev.y + curr.y) / 2;
      const m2x = next ? (curr.x + next.x) / 2 : curr.x;
      const m2y = next ? (curr.y + next.y) / 2 : curr.y;

      ctx2d.beginPath();
      ctx2d.moveTo(m1x, m1y);
      ctx2d.quadraticCurveTo(curr.x, curr.y, m2x, m2y);
      ctx2d.globalAlpha = alpha;
      ctx2d.lineWidth = Math.max(2, bladeTrail.maxWidth * t);
      ctx2d.stroke();
    }
    ctx2d.restore();
    ctx2d.globalAlpha = 1;
  }

  // Juice particles
  for (const p of particles.particles) {
    ctx2d.beginPath();
    ctx2d.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
    ctx2d.fillStyle = p.color;
    ctx2d.globalAlpha = p.life;
    ctx2d.fill();
  }
  ctx2d.globalAlpha = 1;

  // Entities — SVG sprites (debug-circle fallback while textures load).
  // Every sprite gets a soft drop shadow (depth against the camera feed) and
  // fruit/bombs spin while airborne — static translating sprites read as
  // stiff. Spin rate/direction is derived deterministically from entity id.
  const SPRITE_SCALE = 1.7; // sprite radius relative to hit radius

  /**
   * Pre-rendered blob shadow: drawImage-d AFTER translate but BEFORE rotate so
   * the offset stays screen-down regardless of spin angle.
   */
  const drawShadowBlob = (r: number): void => {
    const s = r * 1.15;
    ctx2d.drawImage(
      shadowBlob,
      -s + CONFIG.ui.spriteShadowOffsetX,
      -s + CONFIG.ui.spriteShadowOffsetY,
      s * 2,
      s * 2,
    );
  };
  const spinAngle = (id: number, rateScale = 1): number => {
    const step = (id % 5) - 2 || 1; // -2, -1, 1, 2 — never 0
    return ((step * CONFIG.fruit.spinSpeedMax) / 2) * rateScale * (nowMs / 1000);
  };

  for (const fruit of fruits) {
    const r = fruit.hitRadius * SPRITE_SCALE;
    if (fruit.state === 'whole') {
      const img = textures?.get(fruitWholeKey(fruit.type));
      if (img) {
        ctx2d.save();
        ctx2d.translate(fruit.x, fruit.y);
        if (fruit.isCritical) {
          // Gold halo blob stands in for the expensive critical glow.
          const s = r * 1.45;
          ctx2d.drawImage(goldBlob, -s, -s, s * 2, s * 2);
        } else {
          drawShadowBlob(r);
        }
        ctx2d.rotate(spinAngle(fruit.id));
        ctx2d.drawImage(img, -r, -r, r * 2, r * 2);
        ctx2d.restore();
      } else {
        ctx2d.beginPath();
        ctx2d.arc(fruit.x, fruit.y, fruit.hitRadius, 0, Math.PI * 2);
        ctx2d.strokeStyle = fruit.isCritical ? '#ffd700' : CONFIG.juiceColors[fruit.type];
        ctx2d.lineWidth = 2;
        ctx2d.globalAlpha = 0.7;
        ctx2d.stroke();
        ctx2d.globalAlpha = 1;
      }
    } else if (fruit.state === 'splitting') {
      const split = (fruit as unknown as { _split: SplitHalvesView | null })._split;
      if (split) {
        const age = nowMs - split.splitTime;
        const alpha = Math.max(0, 1 - age / 900);
        const imgA = textures?.get(fruitHalfAKey(fruit.type));
        const imgB = textures?.get(fruitHalfBKey(fruit.type));
        // Halves spin apart in opposite directions as they fall.
        const halfSpin = (age / 1000) * CONFIG.fruit.spinSpeedMax;
        if (imgA) {
          ctx2d.save();
          ctx2d.translate(split.halfA.x, split.halfA.y);
          drawShadowBlob(r);
          ctx2d.rotate(-halfSpin);
          ctx2d.globalAlpha = alpha;
          ctx2d.drawImage(imgA, -r, -r, r * 2, r * 2);
          ctx2d.restore();
        }
        if (imgB) {
          ctx2d.save();
          ctx2d.translate(split.halfB.x, split.halfB.y);
          drawShadowBlob(r);
          ctx2d.rotate(halfSpin);
          ctx2d.globalAlpha = alpha;
          ctx2d.drawImage(imgB, -r, -r, r * 2, r * 2);
          ctx2d.restore();
        }
        ctx2d.globalAlpha = 1;
      }
    }
  }

  // Bombs — slow ominous spin + shadow.
  for (const bomb of bombs) {
    const r = bomb.hitRadius * SPRITE_SCALE;
    const bombImg = textures?.get('bomb');
    if (bombImg) {
      ctx2d.save();
      ctx2d.translate(bomb.x, bomb.y);
      drawShadowBlob(r);
      ctx2d.rotate(spinAngle(bomb.id, 0.5));
      ctx2d.drawImage(bombImg, -r, -r, r * 2, r * 2);
      ctx2d.restore();
    } else {
      ctx2d.beginPath();
      ctx2d.arc(bomb.x, bomb.y, bomb.hitRadius, 0, Math.PI * 2);
      ctx2d.fillStyle = '#1A1A1A';
      ctx2d.fill();
      ctx2d.strokeStyle = '#FF6600';
      ctx2d.lineWidth = 3;
      ctx2d.stroke();
    }
  }

  // Power-ups — shadow only, no spin (icons must stay readable).
  for (const pu of powerUps) {
    const r = pu.hitRadius * SPRITE_SCALE;
    const puImg = textures?.get(pu.type);
    if (puImg) {
      ctx2d.save();
      {
        const s = r * 1.15;
        ctx2d.drawImage(
          shadowBlob,
          pu.x - s + CONFIG.ui.spriteShadowOffsetX,
          pu.y - s + CONFIG.ui.spriteShadowOffsetY,
          s * 2,
          s * 2,
        );
      }
      ctx2d.drawImage(puImg, pu.x - r, pu.y - r, r * 2, r * 2);
      ctx2d.restore();
    } else {
      ctx2d.beginPath();
      ctx2d.arc(pu.x, pu.y, pu.hitRadius, 0, Math.PI * 2);
      ctx2d.strokeStyle = '#00E5FF';
      ctx2d.lineWidth = 3;
      ctx2d.stroke();
    }
  }

  // Dev: visualise the ACTUAL collision radii (dashed rings) — mismatches
  // between what looks hittable and what IS hittable show up instantly.
  if (DEV) {
    ctx2d.save();
    ctx2d.setLineDash([4, 4]);
    ctx2d.lineWidth = 1;
    ctx2d.strokeStyle = 'rgba(57,255,20,0.5)';
    for (const fruit of fruits) {
      if (fruit.state !== 'whole') continue;
      ctx2d.beginPath();
      ctx2d.arc(fruit.x, fruit.y, fruit.hitRadius * CONFIG.slash.fruitHitboxScale, 0, Math.PI * 2);
      ctx2d.stroke();
    }
    ctx2d.strokeStyle = 'rgba(255,102,0,0.6)';
    for (const bomb of bombs) {
      ctx2d.beginPath();
      ctx2d.arc(bomb.x, bomb.y, bomb.hitRadius, 0, Math.PI * 2);
      ctx2d.stroke();
    }
    ctx2d.strokeStyle = 'rgba(0,229,255,0.5)';
    for (const pu of powerUps) {
      ctx2d.beginPath();
      ctx2d.arc(pu.x, pu.y, pu.hitRadius * CONFIG.slash.fruitHitboxScale, 0, Math.PI * 2);
      ctx2d.stroke();
    }
    ctx2d.restore();
  }

  // Pointer cursor (visible in non-PLAYING states via InputRouter)
  const state = stateMachine.state;
  if (InputRouter.cursorVisible(state) && mousePointer) {
    drawCursor(mousePointer.x, mousePointer.y);
  }

  // Faint hand-position dots during PLAYING — the blade trail only appears on
  // movement, so a subtle dot keeps the player oriented between slashes.
  if (state === 'PLAYING') {
    const renderNow = performance.now();
    const interpDot = interp.getCoasted(renderNow, COAST_OPTS);
    for (const hp of activePointers) {
      const age = renderNow - hp.timestamp;
      if (age > CONFIG.tracking.coastMs) continue;
      // Primary hand draws at the interpolated/coasted 60fps position.
      const dx = hp.handIndex === 0 && interpDot ? interpDot.x : hp.x;
      const dy = hp.handIndex === 0 && interpDot ? interpDot.y : hp.y;
      ctx2d.beginPath();
      ctx2d.arc(dx, dy, CONFIG.ui.cursorRadius * 0.6, 0, Math.PI * 2);
      ctx2d.fillStyle = CONFIG.ui.cursorColor;
      ctx2d.globalAlpha = 0.35 * cursorAlpha(age);
      ctx2d.fill();
      ctx2d.globalAlpha = 1;
    }
  }

  ctx2d.restore();
}

function drawCursor(x: number, y: number, alpha = 1): void {
  ctx2d.beginPath();
  ctx2d.arc(x, y, CONFIG.ui.cursorRadius, 0, Math.PI * 2);
  ctx2d.strokeStyle = CONFIG.ui.cursorColor;
  ctx2d.globalAlpha = CONFIG.ui.cursorGlowAlpha * alpha;
  ctx2d.lineWidth = 2;
  ctx2d.stroke();
  ctx2d.globalAlpha = 1;
}

// ---------------------------------------------------------------------------
// Render loop (always-on for menu pointer rendering)
// ---------------------------------------------------------------------------

function menuRenderLoop(): void {
  const state = stateMachine.state;
  if (state !== 'PLAYING') {
    ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    if (InputRouter.cursorVisible(state)) {
      const now = performance.now();

      // Hand-tracking cursors — the user must ALWAYS see where their hand is
      // (blueprint: visual pointer feedback is mandatory). The PRIMARY hand
      // renders from the 60fps interpolator (tracking samples arrive at only
      // ~30fps — drawing them raw looks laggy next to a mouse cursor).
      const interpPos = interp.getCoasted(now, COAST_OPTS);
      let primaryDrawX: number | null = null;
      let primaryDrawY: number | null = null;
      for (const hp of activePointers) {
        const age = now - hp.timestamp;
        if (age >= CONFIG.tracking.coastMs) continue;
        const a = cursorAlpha(age);
        if (hp.handIndex === 0 && interpPos) {
          primaryDrawX = interpPos.x;
          primaryDrawY = interpPos.y;
          drawCursor(interpPos.x, interpPos.y, a);
        } else {
          drawCursor(hp.x, hp.y, a);
        }
      }

      // Dwell progress ring on the primary hand pointer.
      const primary = activePointers[0];
      const prog = dwell.progress(now, CONFIG.dwell.holdDuration);
      if (primary && now - primary.timestamp < CONFIG.tracking.coastMs && prog > 0) {
        const rx = primaryDrawX ?? primary.x;
        const ry = primaryDrawY ?? primary.y;
        ctx2d.beginPath();
        ctx2d.arc(
          rx, ry, CONFIG.dwell.ringRadius,
          -Math.PI / 2, -Math.PI / 2 + prog * Math.PI * 2,
        );
        ctx2d.strokeStyle = CONFIG.ui.cursorColor;
        ctx2d.lineWidth = 4;
        ctx2d.lineCap = 'round';
        ctx2d.stroke();
      }

      // Mouse cursor only when no fresh hand pointer AND the mouse moved
      // recently — hand tracking is the primary input and an idle mouse must
      // neither compete with it nor linger as a phantom second cursor.
      const anyFreshHand = activePointers.some(
        (p) => now - p.timestamp < CONFIG.tracking.handLostDebounceMs,
      );
      const mouseFresh =
        now - lastPointerActivityMs < CONFIG.tracking.mouseIdleHideMs;
      if (mousePointer && mouseFresh && !anyFreshHand) {
        drawCursor(mousePointer.x, mousePointer.y);
      }
    }
  }
  requestAnimationFrame(menuRenderLoop);
}

// ---------------------------------------------------------------------------
// Tracking loop (MediaPipe, runs when camera is active)
// ---------------------------------------------------------------------------

/** Last detectForVideo invocation time — throttles synchronous inference. */
let lastDetectMs = 0;
/** EMA of detectForVideo cost (ms) — input to the adaptive throttle. */
let detectCostEmaMs = 0;
/**
 * Adaptive detect interval (ms): measured cost × budgetFactor, clamped to
 * [expectedIntervalMs, maxIntervalMs]. Slow devices automatically shed
 * tracking rate instead of starving the render loop ("patah-patah").
 */
let detectIntervalMs = CONFIG.tracking.expectedIntervalMs;

/**
 * Process a batch of landmark results (from the main-thread tracker or the
 * worker) and update activePointers, the interpolator, blade trail, and dwell.
 *
 * @param landmarks - Array of per-hand landmark arrays. Each landmark is
 *   a plain {x,y,z} object normalised to [0,1]. Matches
 *   HandLandmarkerResult.landmarks shape and WorkerLandmark[][] shape.
 * @param nowMs     - Wall-clock ms at the moment the result was received on
 *   the main thread (used for pointer timestamps and dwell timing).
 */
function processTrackingResult(
  landmarks: Array<Array<{ x: number; y: number; z: number }>>,
  nowMs: number,
): void {
  const state = stateMachine.state;
  const newPointers: HandPointer[] = [];

  // ── dt for identity gating and the one-euro derivative ────────────────────
  // On the very first call of a session lastProcessTsMs is 0; use the
  // expected tracking interval so the derivative starts at a sane value
  // instead of dt = huge (would spike the one-euro filter open and make the
  // cursor jump to the raw position on the first frame).
  const dtMs = lastProcessTsMs === 0
    ? CONFIG.tracking.expectedIntervalMs
    : nowMs - lastProcessTsMs;
  lastProcessTsMs = nowMs;

  // ── Build candidates for identity gating (RAW normalized coords, pre-mirror)
  // handIdentity.assignHands() uses these to match detections to stable slots,
  // debounce single-frame phantoms, and emit resetFilter when a slot reacquires.
  const candidates: HandCandidate[] = [];
  for (let i = 0; i < landmarks.length; i++) {
    const handLandmarks = landmarks[i];
    if (!handLandmarks) continue;
    const tip = handLandmarks[INDEX_FINGERTIP_LANDMARK];
    if (!tip) continue;
    candidates.push({ tipX: tip.x, tipY: tip.y, index: i });
  }

  // assignHands() gates out single-frame phantoms and returns only
  // confirmed assignments, sorted ascending by slot so newPointers[0]
  // is always slot 0 (primary hand — used by dwell and the interpolator).
  const assignments = assignHands(candidates, nowMs, dtMs)
    .slice()
    .sort((a, b) => a.slot - b.slot);

  for (const assignment of assignments) {
    const handLandmarks = landmarks[assignment.index];
    if (!handLandmarks) continue;
    const tip = handLandmarks[INDEX_FINGERTIP_LANDMARK];
    if (!tip) continue;

    const slot = assignment.slot;
    const smoother = slot === 0 ? smoother0 : smoother1;

    // Reset the one-euro filter (and the slot-0 interpolator) when identity
    // gating signals a new acquisition — prevents the stale previous position
    // from producing a velocity spike that reads as a false slash.
    if (assignment.resetFilter) {
      smoother.reset();
      if (slot === 0) interp.reset();
    }

    const smoothed = smoother.filter({ x: tip.x, y: tip.y }, nowMs / 1000);

    const screen = normToScreen(
      { ...smoothed, z: tip.z },
      canvas.width,
      canvas.height,
    );
    const hp: HandPointer = {
      handIndex: slot,
      x: screen.x,
      y: screen.y,
      rawX: tip.x * canvas.width,
      rawY: tip.y * canvas.height,
      timestamp: nowMs,
    };
    newPointers.push(hp);
    // Second hand's trail is fed at tracking rate; the PRIMARY hand's
    // trail + cursor are fed from the 60fps interpolator in the render
    // loops instead (smoother + lower perceived latency).
    if (state === 'PLAYING' && slot === 1) {
      bladeTrail.push(hp.x, hp.y, nowMs);
    }
    if (slot === 0) {
      interp.push({ x: hp.x, y: hp.y }, nowMs);
    }
  }

  if (newPointers.length > 0) {
    activePointers = newPointers;
  } else {
    // Detection dropout (motion blur on a fast hand): do NOT clear
    // instantly — keep the last pointers until the coast window expires
    // so the cursor/trail glide through the gap instead of blinking out.
    // Gameplay input is unaffected: gesture freshness (handLostDebounceMs)
    // and dwell (fresh detections only) have their own, tighter windows.
    activePointers = activePointers.filter(
      (p) => nowMs - p.timestamp < CONFIG.tracking.coastMs,
    );
  }

  // Dwell check in non-playing states — result fires a synthetic click on
  // the matching button so hand-tracking navigation mirrors mouse clicks.
  if (InputRouter.dwellEnabled(state) && newPointers.length > 0) {
    const primary = newPointers[0];
    if (primary) {
      const ev = dwell.update(primary, nowMs, CONFIG.dwell.holdDuration);
      if (ev) {
        (document.getElementById(ev.targetId))?.click();
      }
    }
  }
}

function trackingLoop(nowMs: number): void {
  // Hard stop: MENU teardown (or a switch to the worker path) clears
  // trackingLoopActive — the chain must die here, NOT keep spinning with a
  // stale landmarker. A zombie chain from session 1 running detectFrame in
  // parallel with session 2's worker pump produced two competing trackers
  // fighting over activePointers.
  if (!trackingLoopActive) return;

  // Guard: never feed a frame-less video to MediaPipe — a 0×0 frame poisons
  // the calculator graph permanently (RET_CHECK roi->width > 0). Covers
  // camera teardown / tab switches even after startCamera()'s readiness wait.
  //
  // THROTTLE: detectForVideo is a SYNCHRONOUS 20-35ms WASM call. Running it
  // on every rAF tick (60Hz) saturates the main thread and starves the render
  // loop — the game then feels laggy even though tracking "works". Gating to
  // ~30fps frees every other frame for rendering. (-4ms tolerance so rAF
  // quantisation lands on every 2nd frame, not every 3rd.)
  if (
    trackerReady() &&
    videoEl.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
    videoEl.videoWidth > 0 &&
    nowMs - lastDetectMs >= detectIntervalMs - 4
  ) {
    lastDetectMs = nowMs;
    trackFrameCount++;
    const t0 = performance.now();
    const result = detectFrame(videoEl, nowMs);
    // Adapt the throttle to the MEASURED inference cost — a phone where one
    // detect takes 50ms must not attempt 30 of them per second.
    const cost = performance.now() - t0;
    const at = CONFIG.perf.adaptiveTracking;
    detectCostEmaMs = detectCostEmaMs === 0
      ? cost
      : detectCostEmaMs + (cost - detectCostEmaMs) * at.emaAlpha;
    detectIntervalMs = Math.min(
      at.maxIntervalMs,
      Math.max(CONFIG.tracking.expectedIntervalMs, detectCostEmaMs * at.budgetFactor),
    );
    if (result) {
      processTrackingResult(result.landmarks, nowMs);
    }
  }

  requestAnimationFrame(trackingLoop);
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

/**
 * Guards against scheduling a second trackingLoop rAF chain when the camera
 * is re-initialised (Menu → ModeSelect round trips). Two concurrent chains
 * feed near-equal timestamps to MediaPipe and latch its graph into a
 * permanent "Packet timestamp mismatch" error state.
 */
let trackingLoopActive = false;

/**
 * 'main' = existing rAF trackingLoop (fallback).
 * 'worker' = dedicated worker pump.
 * null = not yet determined.
 */
let trackingMode: 'main' | 'worker' | null = null;

/**
 * rAF handle for the worker's capture pump (rAF fallback branch only).
 * Kept so we can cancel it if we need to fall back.
 */
let workerPumpRafHandle = 0;

/**
 * Generation token for the worker capture pump. requestVideoFrameCallback
 * chains cannot be cancelled by handle reliably across re-entries, so each
 * pump closure captures the token at start and stops re-registering the
 * moment it goes stale. stopWorkerPump() bumps the token — killing BOTH the
 * rVFC and rAF chains — which prevents the double-pump that used to occur
 * after a worker fatal or a MENU round trip.
 */
let workerPumpToken = 0;

/** Stops any live worker capture pump (rVFC or rAF chain). */
function stopWorkerPump(): void {
  workerPumpToken++;
  if (workerPumpRafHandle !== 0) {
    cancelAnimationFrame(workerPumpRafHandle);
    workerPumpRafHandle = 0;
  }
}

/** Camera/tracker init lifecycle — the READY gate polls this. */
type CameraInitState = 'idle' | 'starting' | 'ready' | 'failed';
let cameraInitState: CameraInitState = 'idle';

/** Initialises camera + tracker lazily after user consent. */
function initCameraAndTracker(): void {
  if (cameraInitState === 'starting' || cameraInitState === 'ready') return;
  cameraInitState = 'starting';

  startCamera(videoEl).then((stream) => {
    cameraStream = stream;

    const trackerOpts = {
      numHands: IS_MOBILE ? CONFIG.perf.mobile.numHands : 2,
      minHandDetectionConfidence: CONFIG.tracking.confidence.detection,
      minHandPresenceConfidence: CONFIG.tracking.confidence.presence,
      minTrackingConfidence: CONFIG.tracking.confidence.tracking,
    };

    if (isWorkerTrackingSupported() && trackingMode === null) {
      // Attempt the off-main-thread worker path first.
      return workerTrackerInit(trackerOpts, {
        onResult(landmarks, tsMs, costMs) {
          trackFrameCount++;
          // Update EMA for the debug overlay (same as the main-thread path).
          const at = CONFIG.perf.adaptiveTracking;
          detectCostEmaMs = detectCostEmaMs === 0
            ? costMs
            : detectCostEmaMs + (costMs - detectCostEmaMs) * at.emaAlpha;
          // Use the frame CAPTURE timestamp rather than arrival time.
          // Arrival time adds variable inference-latency jitter (~20-30ms) to
          // the inter-frame dt fed into the one-euro derivative and gesture
          // velocity — causing erratic smoothing and false slash events.
          // Capture time (stamped by sendFrame at the camera frame boundary)
          // restores the even ~33ms cadence the tracker expects, matching the
          // main-thread fallback path which already passes capture-time nowMs.
          // A ~20-30ms capture-to-arrival offset is well within the pointer
          // freshness windows (handLostDebounceMs 300ms, coastMs 450ms).
          const ts = tsMs > 0 ? tsMs : performance.now();
          processTrackingResult(landmarks, ts);
        },
        onFatal() {
          console.warn('[Fruit Slasher] Worker tracker fatal — falling back to main-thread path.');
          // Stop the worker pump and switch to the fallback synchronously.
          stopWorkerPump();
          workerTrackerDestroy();
          // The switch gap (worker death → main tracker ready) can span
          // hundreds of ms. Stale identity slots + lastProcessTsMs would make
          // the first fallback frame compute a giant dt — blowing the identity
          // match radius wide open and spiking the one-euro derivative.
          resetHandIdentity();
          lastProcessTsMs = 0;
          trackingMode = 'main';
          startMainThreadTracker();
        },
      }).then(() => {
        trackingMode = 'worker';
        startWorkerPump();
      }).catch((err: unknown) => {
        console.warn('[Fruit Slasher] Worker tracker init failed — falling back.', err);
        workerTrackerDestroy();
        trackingMode = 'main';
        return initHandTracker(undefined, trackerOpts);
      });
    }

    // Fallback (or worker already tried and failed).
    trackingMode = 'main';
    return initHandTracker(undefined, trackerOpts);

  }).then(() => {
    // Reveal the mirrored camera feed (semi-transparent game background).
    videoEl.style.opacity = String(
      IS_MOBILE ? CONFIG.perf.mobile.videoOpacity : CONFIG.ui.videoOpacity,
    );
    cameraInitState = 'ready';

    if (trackingMode === 'main' && !trackingLoopActive) {
      trackingLoopActive = true;
      requestAnimationFrame(trackingLoop);
    }
    // Worker mode: pump was already started inside the .then() above.
  }).catch((err: unknown) => {
    cameraInitState = 'failed';
    if (err instanceof CameraError) {
      console.warn(`[Fruit Slasher] Camera unavailable (${err.reason}) — mouse fallback active`);
    } else {
      console.warn('[Fruit Slasher] Tracker init failed — mouse fallback active', err);
    }
    // Game is still playable via mouse/touch fallback — no fatal error.
  });
}

/** Starts the main-thread rAF tracking loop (fallback path). */
function startMainThreadTracker(): void {
  const trackerOpts = {
    numHands: IS_MOBILE ? CONFIG.perf.mobile.numHands : 2,
    minHandDetectionConfidence: CONFIG.tracking.confidence.detection,
    minHandPresenceConfidence: CONFIG.tracking.confidence.presence,
    minTrackingConfidence: CONFIG.tracking.confidence.tracking,
  };
  void initHandTracker(undefined, trackerOpts).then(() => {
    if (!trackingLoopActive) {
      trackingLoopActive = true;
      requestAnimationFrame(trackingLoop);
    }
  });
}

/**
 * Starts the frame capture pump for the worker path.
 *
 * Prefers requestVideoFrameCallback (fires in sync with the camera's actual
 * frame cadence — no adaptive throttle needed) with a rAF chain fallback.
 *
 * Backpressure is handled inside sendFrame (returns false when a frame is
 * already in-flight) so effective rate = min(camera fps, inference rate).
 */
function startWorkerPump(): void {
  stopWorkerPump(); // never two live chains, even on rapid re-entry
  const token = workerPumpToken;

  // requestVideoFrameCallback is not yet universally available.
  if ('requestVideoFrameCallback' in videoEl) {
    const pump = (_now: DOMHighResTimeStamp, _meta: VideoFrameCallbackMetadata): void => {
      if (token !== workerPumpToken) return; // pump stopped — do not re-register
      sendFrame(videoEl, performance.now());
      videoEl.requestVideoFrameCallback(pump);
    };
    videoEl.requestVideoFrameCallback(pump);
  } else {
    // rAF fallback — same cadence as the existing trackingLoop.
    const rafPump = (): void => {
      if (token !== workerPumpToken) return;
      sendFrame(videoEl, performance.now());
      workerPumpRafHandle = requestAnimationFrame(rafPump);
    };
    workerPumpRafHandle = requestAnimationFrame(rafPump);
  }
}

// ---------------------------------------------------------------------------
// READY gate flow — camera ready → hand confirmed → countdown → PLAYING
// ---------------------------------------------------------------------------

/** Invalidates in-flight ready flows on re-entry (retry spam, MENU exits). */
let readyFlowToken = 0;
/** Set by the skip button — jumps straight to the countdown (touch mode). */
let readySkip = false;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

type ReadyWait = 'met' | 'timeout' | 'aborted';

/** Polls `cond` every 100ms while we remain in THIS flow's READY state. */
async function waitInReady(
  cond: () => boolean,
  timeoutMs: number,
  token: number,
): Promise<ReadyWait> {
  const start = performance.now();
  for (;;) {
    if (token !== readyFlowToken || stateMachine.state !== 'READY') return 'aborted';
    if (cond()) return 'met';
    if (performance.now() - start >= timeoutMs) return 'timeout';
    await sleep(100);
  }
}

async function runReadyFlow(): Promise<void> {
  const token = ++readyFlowToken;
  readySkip = false;
  readyCountEl.textContent = '';
  readySkipBtn.style.display = '';

  // Phase 1 — camera + tracker init (instant on retry: camera stays alive).
  readyMsgEl.textContent = STRINGS.ready.startingCamera;
  const inited = await waitInReady(
    () => readySkip || cameraInitState === 'ready' || cameraInitState === 'failed',
    CONFIG.ready.cameraWaitTimeoutMs,
    token,
  );
  if (inited === 'aborted') return;

  // Phase 2 — require a CONTINUOUSLY tracked hand before fruit ever flies.
  // This is the fix for "game started but the camera wasn't ready": lives
  // were being lost against an input source that did not exist yet.
  let handMode = false;
  if (!readySkip && cameraInitState === 'ready') {
    readyMsgEl.textContent = STRINGS.ready.showHand;
    let freshSince = 0;
    const confirmed = await waitInReady(() => {
      if (readySkip) return true;
      const now = performance.now();
      const fresh = activePointers.some(
        (p) => now - p.timestamp < CONFIG.tracking.handLostDebounceMs,
      );
      if (!fresh) {
        freshSince = 0;
        return false;
      }
      if (freshSince === 0) freshSince = now;
      return now - freshSince >= CONFIG.ready.handConfirmMs;
    }, CONFIG.ready.handWaitTimeoutMs, token);
    if (confirmed === 'aborted') return;
    handMode = confirmed === 'met' && !readySkip;
  }

  readyMsgEl.textContent = handMode
    ? STRINGS.ready.handDetected
    : STRINGS.ready.touchModeNote;
  readySkipBtn.style.display = 'none';

  // Phase 3 — countdown. The player watches their cursor work the whole time.
  for (let n = CONFIG.ready.countdownFrom; n >= 1; n--) {
    readyCountEl.textContent = String(n);
    await sleep(CONFIG.ready.countdownStepMs);
    if (token !== readyFlowToken || stateMachine.state !== 'READY') return;
  }
  stateMachine.transition('PLAYING');
}

function bootstrap(): void {
  // Show start screen immediately — user must consent BEFORE camera request (T-01).
  startPanel.style.display = 'flex';
  menuRenderLoop();

  // Load SVG textures in the background; render falls back to debug circles
  // until the registry is ready (usually well before the first game starts).
  void loadTextures().then((registry) => {
    textures = registry;
  });

  // Dev-only diagnostics overlay (bottom-right): FPS, pointer velocity,
  // entity counts vs cap, and per-slash HIT/MISS log with nearest-gap data.
  if (DEV) {
    initDebugOverlay();
    setInterval(() => {
      const pts = bladeTrail.points;
      let velocity = 0;
      if (pts.length >= 2) {
        const a = pts[pts.length - 2];
        const b = pts[pts.length - 1];
        if (a && b && b.timestamp > a.timestamp) {
          velocity = (Math.hypot(b.x - a.x, b.y - a.y) / (b.timestamp - a.timestamp)) * 1000;
        }
      }
      let airborne = 0;
      for (const f of fruits) {
        if (f.state === 'whole') airborne++;
      }
      setDebugStats({
        renderFps: renderFrameCount * 2,
        trackFps: trackFrameCount * 2,
        velocity,
        airborne,
        cap:
          CONFIG.game.concurrentStart +
          Math.floor(gameElapsedMs / CONFIG.game.concurrentRampMs),
        bombs: bombs.length,
        powerUps: powerUps.length,
        state: stateMachine.state,
        detectMs: detectCostEmaMs,
        detectIntervalMs,
      });
      renderFrameCount = 0;
      trackFrameCount = 0;
    }, 500);
  }

  // Camera init is deferred to the first MODESELECT transition (explicit user
  // consent); the READY gate also kicks it as a safety net. Returning to MENU
  // stops the camera (privacy) — the READY gate absorbs the re-init wait.
  stateMachine.onTransition((next) => {
    if (next === 'MODESELECT') {
      initCameraAndTracker();
    }
    if (next === 'MENU' && cameraStream !== null) {
      stopCamera(cameraStream);
      cameraStream = null;
      cameraInitState = 'idle';
      // Full tracker teardown — both paths. Leaving a zombie rAF chain or a
      // live worker behind meant session 2 could run TWO trackers at once
      // (stale main-thread landmarker + fresh worker), each updating
      // activePointers on alternating frames.
      stopWorkerPump();
      workerTrackerDestroy();
      trackingLoopActive = false;
      destroyHandTracker();
      trackingMode = null; // re-attempt worker path on next READY cycle
      videoEl.style.opacity = '0';
      // Clear hand-identity slot continuity and dt baseline so the next
      // session reacquires from scratch (no stale slot from the previous
      // camera session biasing the nearest-neighbour match radius).
      resetHandIdentity();
      lastProcessTsMs = 0;
    }
  });
}

bootstrap();
